import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Delete, DeleteOutlined, EditOutlined, Person, Star } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";
import { CustomerReference } from "models/Order";
import EditCustomerReferenceDialog from "../dialogs/EditCustomerReferenceDialog";
import { formatDateTime } from "utilities/formatDateTime";
import SimpleDialog from "components/SimpleDialog";
import { useDeleteCustomerReferenceMutation } from "app/api/orders";
import EmptyMessage from "components/EmptyMessage";

type ReferenceSectionProps = { orderId: string; customerReference: CustomerReference | null };

const ReferenceSection: React.FC<ReferenceSectionProps> = (props) => {
  const { orderId, customerReference } = props;
  const { user } = useAuthContext();

  const [showEditReferenceDialog, setShowEditReferenceDialog] = useState(false);
  const [showDeleteReferenceDialog, setShowDeleteReferenceDialog] = useState(false);

  const [deleteCustomerReference] = useDeleteCustomerReferenceMutation();

  const userCanEdit =
    user?.role === UserRole.ADMIN ||
    user?.role === UserRole.USER_ADMIN ||
    user?.role === UserRole.SALES;

  return (
    <Box>
      {!customerReference && (
        <Box px={2} pb={1}>
          {userCanEdit ? (
            <Button
              hidden={!userCanEdit}
              variant="text"
              startIcon={<Star />}
              onClick={() => setShowEditReferenceDialog(true)}
              disabled={!userCanEdit}
            >
              Registrera
            </Button>
          ) : (
            <EmptyMessage>Ej registrerat</EmptyMessage>
          )}
        </Box>
      )}

      {customerReference && (
        <Stack
          sx={(theme) => ({
            maxWidth: "max-content",
            marginInline: "auto",
            [theme.breakpoints.up("sm")]: {
              display: "grid",
              gridAutoFlow: "column",
              marginLeft: 0,
            },
          })}
        >
          <ListItem>
            <ListItemAvatar>
              <Star sx={{ fontSize: 56, color: "#ffbf00ff" }} />
            </ListItemAvatar>
            <ListItemText
              primary={<Typography fontWeight="bold">Registrerat</Typography>}
              secondary={
                <>
                  <Link
                    component={RouterLink}
                    to={customerReference.createdBy.userId}
                    fontSize="small"
                  >
                    {customerReference.createdBy.userName}
                  </Link>
                  <Typography fontSize="small">
                    {formatDateTime(customerReference.createdAt)}
                  </Typography>
                </>
              }
            />
          </ListItem>

          {customerReference.contact && (
            <ListItem>
              <ListItemAvatar>
                <Person sx={{ fontSize: 56, color: "primary.main" }} />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography fontWeight="bold">{customerReference.contact.name}</Typography>
                }
                secondary={
                  <>
                    <Typography fontSize="small">{customerReference.contact.email}</Typography>
                    <Typography fontSize="small">{customerReference.contact.phone}</Typography>
                  </>
                }
              />
            </ListItem>
          )}

          {customerReference && userCanEdit && (
            <ListItem sx={{ justifyContent: "center" }}>
              <Tooltip title="Avregistrera referensobjekt">
                <IconButton onClick={() => setShowDeleteReferenceDialog(true)}>
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Redigera referensperson">
                <IconButton onClick={() => setShowEditReferenceDialog(true)}>
                  <EditOutlined />
                </IconButton>
              </Tooltip>
            </ListItem>
          )}
        </Stack>
      )}

      {showEditReferenceDialog && (
        <EditCustomerReferenceDialog
          orderId={orderId}
          isReference={customerReference !== null}
          contact={customerReference?.contact || null}
          onClose={() => setShowEditReferenceDialog(false)}
        />
      )}

      {showDeleteReferenceDialog && (
        <SimpleDialog
          title="Ta bort referensobjekt"
          message="Är du säker på att du vill ta bort detta referensobjekt och eventuell kontaktperson?"
          confirmProps={{
            children: "Ta bort",
            color: "error",
            startIcon: <Delete />,
          }}
          onClose={() => setShowDeleteReferenceDialog(false)}
          onConfirm={() => {
            deleteCustomerReference({ orderId });
            setShowDeleteReferenceDialog(false);
          }}
        />
      )}
    </Box>
  );
};

export default ReferenceSection;
